import { Chip, Grid, Typography } from "@material-ui/core";
import { Delete, Edit } from "@material-ui/icons";
import { Handle, Position, useEdges, useNodesInitialized, useUpdateNodeInternals } from "@xyflow/react";
import { useContext, useEffect } from "react";
import { CalculationVisualizerContext } from "../../../Context/CalculationVisualizerContextProvider";
import { setCustomCalculations } from "../../../Actions";

function convertToPricingFormat(value) {
  return parseFloat(Number(value).toFixed(2));
}

export default function CustomNode ({ data, height, width }) {
  const { isPromoFlow, includesPricing, graph, setRefreshGraph, setSelectedNode, setIsEditNodeModalOpen } = useContext(CalculationVisualizerContext);
  const edges = useEdges();
  const areNodesInitialized = useNodesInitialized();
  const updateNodeInternals = useUpdateNodeInternals();

  const nodeData = data.nodeData || {};

  useEffect(() => {
    if (!areNodesInitialized) return;

    updateNodeInternals(data.id);
  }, [areNodesInitialized])

  const targetHandles = [];
  const targetEdges = edges.filter(edge => edge.target === data.id) ?? [];
  const isSameNext = targetEdges.every(edge => edge.source === targetEdges[0].source);
  const targetOffsetHeight = (nodeData.orientation === 'h' ? height : width) / (targetEdges.length + 1);

  if (targetEdges?.length > 1) {
    targetHandles.push(
      ...targetEdges.map((edge, index) => {
        let offset;

        if (isSameNext) {
          offset = (index + 1) * (targetOffsetHeight || 40);
        } else {
          offset = (index + 1) * (targetOffsetHeight || 20)
        }

        return (
          <Handle 
            key={edge.targetHandle} 
            type="target" 
            id={edge.targetHandle} 
            style={
              nodeData.orientation === 'h' ? (
                { top: offset + 'px', left: "0px" }
              ) : (
                { left: offset + 'px' }
              )
            } 
            position={nodeData.orientation === 'h' ? Position.Left : Position.Top} 
          />
        )
      })
    )
  } else if (data.id !== 'start') {
    targetHandles.push(
      <Handle 
        key={targetEdges[0]?.targetHandle + '-target'} 
        type="target" 
        id={targetEdges[0]?.targetHandle} 
        style={
          nodeData.orientation === 'h' ? (
            { top: targetOffsetHeight ? targetOffsetHeight + 'px' : 'initial' }
          ) : (
            { left: targetOffsetHeight ? targetOffsetHeight + 'px' : 'initial' }
          )
        } 
        position={nodeData.orientation === 'h' ? Position.Left : Position.Top} 
      />
    );
  }

  const sourceHandles = [];
  const sourceOffsetHeight = (nodeData.orientation === 'h' ? height : width) / ((nodeData.condition?.operands?.length || 0) + 2);

  if (nodeData.condition?.operands?.length) {
    sourceHandles.push(
      ...nodeData.condition.operands.map((operand, index) => {
        const offset = ((index + 1) * (sourceOffsetHeight || 20));

        return (
          <Handle 
            key={operand.next + `-${index + 2}`} 
            type="source" 
            position={nodeData.orientation === 'h' ? Position.Right : Position.Bottom} 
            id={operand.next + `-${index + 2}`} 
            style={
              nodeData.orientation === 'h' ? (
                { top: offset + 'px', right: "0px" }
              ) : (
                { left: offset + 'px' }
              )
            }
          />
        )
      })
    );
  }

  if (nodeData.condition?.default?.next) {
    sourceHandles.push(
      <Handle 
        key={nodeData.condition?.default?.next + '-default'}
        type="source" 
        position={nodeData.orientation === 'h' ? Position.Right : Position.Bottom} 
        id={nodeData.condition?.default?.next + '-default'} 
        style={
          nodeData.orientation === 'h' ? (
            { top: (nodeData.condition?.operands?.length ? (nodeData.condition.operands.length + 1) * (sourceOffsetHeight || 20) : 0) + 'px', right: "0px" }
          ) : (
            { left: (nodeData.condition?.operands?.length ? (nodeData.condition.operands.length + 1) * (sourceOffsetHeight || 20) : 0) + 'px' }
          )
        }
      />
    );
  }

  if (nodeData.next) {
    sourceHandles.push(
      <Handle 
        key={nodeData.next + '-source'} 
        type="source" 
        style={
          nodeData.orientation === 'h' ? (
            { top: sourceOffsetHeight ? sourceOffsetHeight + 'px' : 'initial' }
          ) : (
            { left: sourceOffsetHeight ? sourceOffsetHeight + 'px' : 'initial' }
          )
        } 
        position={nodeData.orientation === 'h' ? Position.Right : Position.Bottom} 
        id={nodeData.next} 
      />
    );
  }

  if (includesPricing) {
    const pricingType = isPromoFlow ? 'promotion' : 'standard';
    const pricingValue = nodeData.pricingData?.[pricingType]?.formulaEval?.result || nodeData.pricingData?.[pricingType]?.value || null;

    return (
      <div key={data.id}>
        {targetHandles}

        <Grid container spacing={1} style={{ textAlign: 'left' }}> 
          <Grid item xs={12} style={{ textAlign: 'left' }}>
            {nodeData.group && (
              <Chip
                size="small"
                style={{marginRight: "10px"}}
                label={nodeData.group}
                variant="outlined"
              />  
            )} 

            {nodeData.condition && (
              <Chip
                size="small"
                style={{marginRight: "10px"}}
                label={"Condition"}
                variant="outlined"
              />  
            )}

            {nodeData.formula && (
              <Chip
                size="small"
                style={{marginRight: "10px"}}
                label={"Formula"}
                variant="outlined"
              />  
            )} 
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6">
              {data.label}
            </Typography>
          </Grid>  

          {['start', 'end'].includes(data.id) && nodeData.pricingData ? (
              <Grid item xs={12} style={{ textAlign: 'left', color: isPromoFlow ? '#f44336' : 'initial' }}>
                <Typography variant="subtitle2" style={{display: 'block', marginTop: "10px"}}>{pricingValue ? 'Output: $' + pricingValue : ''}</Typography>
              </Grid>
            ) : undefined
          }

          {nodeData.formula && nodeData.pricingData ? (
              <Grid item xs={12} style={{ textAlign: 'left', color: isPromoFlow ? '#f44336' : 'initial' }}>
                { !nodeData.pricingData?.[pricingType]?.isPriceOverriden ? (
                  <>
                    <Typography variant="caption" style={{display: 'block'}}>
                      Used the <u>{nodeData.formula}</u> formula with value <u>{nodeData.pricingData?.[pricingType]?.formulaEval?.valueAdded}</u>
                    </Typography> 
                    <Typography variant="subtitle2" style={{display: 'block', marginTop: "10px"}}>{pricingValue ? 'Output: $' + pricingValue : ''}</Typography>
                  </>
                ) : (
                  <Typography variant="caption" style={{display: 'block'}}>
                    Not applicable due to price override
                  </Typography>
                )}
              </Grid>
            ) : undefined
          }
        </Grid>

        {sourceHandles}
      </div>
    );
  }

  const handleNodeDeletion = async () => {
    const next = data.nodeData.condition?.default?.next || data.nodeData.next;
    const tempGraph = { ...graph };

    targetEdges.forEach(edge => {
      if (tempGraph[edge.source].condition) {
        if (tempGraph[edge.source].condition.default?.next === data.id) {
          tempGraph[edge.source].condition.default.next = next;
        }

        tempGraph[edge.source].condition.operands?.forEach((operand, i) => {
          if (operand.next === data.id) tempGraph[edge.source].condition.operands[i].next = next;
        });
      } else {
        tempGraph[edge.source].next = next;
      }
    });

    delete tempGraph[data.id];

    await setCustomCalculations(JSON.stringify(tempGraph));
    setRefreshGraph(true);
  }

  return (
    <div key={data.id}>
      {targetHandles}

      <Grid container spacing={1} style={{ textAlign: 'left' }}>

        <Grid item xs={8} style={{ textAlign: 'left' }}>
          {nodeData.group && (
            <Chip
              size="small"
              style={{marginRight: "10px"}}
              label={nodeData.group}
              variant="outlined"
            />  
          )} 

          {nodeData.condition && (
            <Chip
              size="small"
              style={{marginRight: "10px"}}
              label={"Condition"}
              variant="outlined"
            />  
          )}

          {nodeData.formula && (
            <Chip
              size="small"
              style={{marginRight: "10px"}}
              label={"Formula"}
              variant="outlined"
            />  
          )} 
        </Grid>

        {!['start', 'end'].includes(data.id) && (
          <Grid item xs={2} style={{ textAlign: 'right' }}>
            <Edit 
              style={{ cursor: 'pointer' }}
              onClick={() => {
                const nodeData = {
                  ...graph[data.id],
                  id: data.id
                }

                if (nodeData.formula) {
                  nodeData.operand = {
                    formula: nodeData.formula,
                    value: nodeData.value,
                    attribute: nodeData.attribute,
                    inputAttribute: nodeData.inputAttribute,
                  }
                }

                setSelectedNode(nodeData);
                setIsEditNodeModalOpen(true);
              }}
            />
          </Grid> 
        )}

        {!['start', 'end'].includes(data.id) && (
          <Grid item xs={2} style={{ textAlign: 'right' }}>
            <Delete 
              style={{ color: '#f44336', cursor: 'pointer' }}
              onClick={() => handleNodeDeletion()}
            />
          </Grid> 
        )}

        <Grid item xs={12}>
          <Typography variant="h6">
            {data.label}
          </Typography>
        </Grid>  

        {nodeData.condition?.operator && (
          <Grid item xs={12}>
            <Typography variant="caption">
              Operator: {nodeData.condition?.operator}
            </Typography>
          </Grid>
        )}
        {nodeData.inputAttribute && (
          <Grid item xs={12}>
            <Typography variant="caption">
              Input Attribute: {nodeData.inputAttribute}
            </Typography>
          </Grid>
        )}
        {nodeData.attribute && (
          <Grid item xs={12}>
            <Typography variant="caption">
              Attribute: {nodeData.attribute}
            </Typography>
          </Grid>
        )}
        {nodeData.formula && (
          <Grid item xs={12}>
            <Typography variant="caption">
              Formula: {nodeData.formula}
            </Typography>
          </Grid>
        )}
        {nodeData.value !== undefined && (
          <Grid item xs={12}>
            <Typography variant="caption">
              Value: {nodeData.value}
            </Typography>
          </Grid>
        )}
      </Grid>

      {sourceHandles}
    </div>
  );
};